import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { BROCHURE_MODULES, type ModuleSlug } from "../../data/brochureModules";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import AttendanceBrochure from "./AttendanceBrochure";
import LeavesBrochure from "./LeavesBrochure";
import DevelopmentBrochure from "./DevelopmentBrochure";
import PayrollBrochure from "./PayrollBrochure";
import RecruitmentBrochure from "./RecruitmentBrochure";
import ImpactBrochure from "./ImpactBrochure";
import TournamentBrochure from "./TournamentBrochure";
import ProjectsBrochure from "./ProjectsBrochure";

/**
 * Phase 6 brochure router.
 *
 * Replaces the Phase 5 placeholder at /brochure/:moduleSlug. Reads the slug
 * from URL params, dispatches to the matching brochure component. The
 * Attendance and Leaves brochures land here from this PR; the remaining 6
 * modules ship via parallel subagents using the same `ModuleBrochure`
 * template — until then they render a quiet "Coming soon" card so the route
 * never 404s and the BrochureOverlay CTAs continue to work.
 *
 * If the slug is unknown (typo, deep link from a stale URL), we render a
 * graceful "module not found" affordance with a way back to the workspace.
 */

const KNOWN_SLUGS = new Set<ModuleSlug>(BROCHURE_MODULES.map((m) => m.slug));

// ---------- Coming-soon fallback for slugs not yet implemented ----------

interface ComingSoonProps {
  slug: ModuleSlug;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept around as fallback if a new ModuleSlug ships before its brochure does
const BrochureComingSoon: React.FC<ComingSoonProps> = ({ slug }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.dir() === "rtl";
  const moduleEntry = BROCHURE_MODULES.find((m) => m.slug === slug);
  const moduleName = moduleEntry ? t(moduleEntry.nameKey) : slug;

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        {moduleEntry && (
          <div
            className={cn(
              "mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl",
              moduleEntry.accent.surface,
            )}
          >
            <Icon
              name={moduleEntry.iconName}
              size={28}
              className={moduleEntry.accent.iconText}
              aria-hidden="true"
            />
          </div>
        )}
        <h1 className="mb-2 text-center text-2xl font-semibold text-slate-900">
          {t("brochure.comingSoon.heading", { module: moduleName })}
        </h1>
        <p className="mb-6 text-center text-sm leading-relaxed text-slate-600">
          {t("brochure.comingSoon.body")}
        </p>
        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/settings/billing")}
            className={cn(
              "inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white",
              "shadow-sm hover:bg-indigo-700",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            )}
          >
            {t("See plans")}
            <Icon
              name="ArrowRight"
              size={14}
              className={isRtl ? "rotate-180" : ""}
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            {t("brochure.back")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Unknown-slug fallback ----------

const BrochureUnknown: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
          <Icon name="HelpCircle" size={28} className="text-slate-500" aria-hidden="true" />
        </div>
        <h1 className="mb-2 text-center text-2xl font-semibold text-slate-900">
          {t("Module not found")}
        </h1>
        <p className="mb-6 text-center text-sm leading-relaxed text-slate-600">
          {t("We couldn't find a brochure for this module.")}
        </p>
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className={cn(
              "inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white",
              "shadow-sm hover:bg-slate-800",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2",
            )}
          >
            {t("brochure.backToWorkspace")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Router ----------

const BrochureRouter: React.FC = () => {
  const { moduleSlug } = useParams<{ moduleSlug: string }>();
  const slug = moduleSlug as ModuleSlug | undefined;
  const isKnown = !!slug && KNOWN_SLUGS.has(slug);

  if (!isKnown || !slug) return <BrochureUnknown />;

  switch (slug) {
    case "attendance":
      return <AttendanceBrochure />;
    case "leaves":
      return <LeavesBrochure />;
    case "development":
      return <DevelopmentBrochure />;
    case "payroll":
      return <PayrollBrochure />;
    case "recruitment":
      return <RecruitmentBrochure />;
    case "impact":
      return <ImpactBrochure />;
    case "tournament":
      return <TournamentBrochure />;
    case "projects":
      return <ProjectsBrochure />;
    default: {
      // Exhaustiveness check — if a new ModuleSlug is added without a case,
      // this line becomes a compile error. Keeps the router honest.
      const _exhaustive: never = slug;
      void _exhaustive;
      return <BrochureUnknown />;
    }
  }
};

export default BrochureRouter;
